import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { putToSignedUrl } from '../api';
import { useWorkstation } from '../auth';
import { Thumbnail } from '../components/Thumbnail';
import { jobNameFrom, jobsView, selectionState } from '../jobs';
import type { Job, PrinterSummary } from '../model';
import {
  defaultPrinterId, orderPrinters, printerOptionLabel, showSurveyWarning,
} from '../printers';
import { renderThumbnail } from '../thumbnail';

/**
 * Create a job, upload its design, list what exists (F1).
 *
 * A job is one design PDF. Sheets are added to it on the job page, one per
 * piece of canvas, because the client prints the same design on several.
 */
export function Jobs() {
  const api = useWorkstation();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [printers, setPrinters] = useState<PrinterSummary[]>([]);
  // The survey warning waits for the list, so it does not flash on load.
  const [printersLoaded, setPrintersLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(params.has('archived'));
  const [archiving, setArchiving] = useState<string | null>(null);
  // Ticked active jobs, for archiving several at once.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const selectAllBox = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [mesh, setMesh] = useState(18);
  const [printerId, setPrinterId] = useState('default');
  const [file, setFile] = useState<File | null>(null);

  const reload = () => {
    api.listJobs().then((r) => setJobs(r.jobs)).catch((e) => setError(e.message));
  };
  useEffect(reload, []);
  useEffect(() => {
    api.listPrinters()
      .then((r) => {
        // Ready printers first, so the default is one that can print.
        const ordered = orderPrinters(r.printers);
        setPrinters(ordered);
        setPrinterId(defaultPrinterId(ordered));
        setPrintersLoaded(true);
      })
      .catch(() => { /* the default printer id still works */ });
  }, []);

  async function create(event: React.FormEvent) {
    event.preventDefault();
    if (!file) { setError('choose the design PDF first'); return; }
    setBusy(true);
    setError(null);
    try {
      const job = await api.createJob({
        name: jobNameFrom(name, file.name), mesh, printerId, filename: file.name,
      });
      // Upload straight to S3. The design can be tens of megabytes and the
      // API would refuse it long before S3 would.
      const { url } = await api.artworkUpload(job.jobId);
      await putToSignedUrl(url, file, 'application/pdf');
      // The thumbnail is drawn here, where the file already is. A design that
      // pdf.js cannot draw is still a job; the page shows a blank instead.
      try {
        const png = await renderThumbnail(file);
        const thumb = await api.thumbnailUpload(job.jobId);
        await putToSignedUrl(thumb.url, png, 'image/png');
      } catch (e) {
        console.warn('thumbnail not drawn:', (e as Error).message);
      }
      // Straight to the job, ready for its first photo: sheet 1 for a lid
      // camera, the QR code for the phone (see the Job page).
      navigate(`/jobs/${job.jobId}?new=1`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function archive(job: Job, archived: boolean) {
    // Restoring needs no confirm: it only brings a job back.
    if (archived && !window.confirm(
      `Archive "${job.name}"?\n\nIt leaves the jobs list. Nothing is deleted, `
      + 'and it can be restored from "Show archived".')) return;
    const { jobId } = job;
    setArchiving(jobId);
    setError(null);
    try {
      await api.archiveJob(jobId, archived);
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setArchiving(null); }
  }

  /**
   * Archive every ticked job, with one confirm for the lot.
   *
   * One request per job against the same endpoint as the row button, rather
   * than a batch endpoint: a shop clears tens of jobs, not thousands, and the
   * per-job route already carries who archived it and when. Each one stands
   * alone, so a failure leaves the rest archived and the failed ones still
   * ticked for another try.
   */
  async function archiveSelected() {
    const picked = active.filter((j) => selected.has(j.jobId));
    if (picked.length === 0) return;
    if (!window.confirm(
      `Archive ${picked.length} job${picked.length === 1 ? '' : 's'}?\n\n`
      + 'They leave the jobs list. Nothing is deleted, and each can be '
      + 'restored from "Show archived".')) return;
    setBulkBusy(true);
    setBulkError(null);
    const results = await Promise.allSettled(
      picked.map((j) => api.archiveJob(j.jobId, true)));
    const failed = picked.filter((_, i) => results[i].status === 'rejected');
    setSelected(new Set(failed.map((j) => j.jobId)));
    if (failed.length > 0) {
      const why = (results.find((r) => r.status === 'rejected') as PromiseRejectedResult)
        .reason as Error;
      setBulkError(`${failed.length} of ${picked.length} could not be archived `
        + `(${why.message}). They are still ticked; try again.`);
    }
    setBulkBusy(false);
    reload();
  }

  function toggle(jobId: string) {
    setSelected((was) => {
      const next = new Set(was);
      if (!next.delete(jobId)) next.add(jobId);
      return next;
    });
  }

  // `jobs.ts` decides what is shown. See `jobsView`.
  const { active, archived, emptyMessage } =
    jobsView(jobs ?? [], params.toString());
  const activeIds = active.map((j) => j.jobId);
  const selection = selectionState(activeIds, selected);
  // `indeterminate` has no attribute; it can only be set on the element.
  useEffect(() => {
    if (selectAllBox.current) selectAllBox.current.indeterminate = selection.some;
  });
  const warnNoSurvey = printersLoaded && showSurveyWarning(printers);

  return (
    <div className="columns">
      <div className="stack side">
        <section className="panel">
          <h2>New job</h2>
          <form className="stack" onSubmit={create}>
            <label>
              Design PDF
              <input type="file" accept="application/pdf" required
                     onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
            <label>
              Name <span className="muted">(optional, defaults to the file name)</span>
              <input value={name} maxLength={60}
                     placeholder={file ? jobNameFrom('', file.name) : 'Autumn ornaments'}
                     onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              Mesh count
              <select value={mesh} onChange={(e) => setMesh(Number(e.target.value))}>
                <option value={18}>18 — 18 stitches per inch</option>
                <option value={13}>13 — 13 stitches per inch</option>
              </select>
            </label>
            {printers.length > 0 && (
              <label>
                Printer
                <select value={printerId}
                        onChange={(e) => setPrinterId(e.target.value)}>
                  {printers.map((p) => (
                    <option key={p.printerId} value={p.printerId}>
                      {printerOptionLabel(p)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button className="primary" type="submit" disabled={busy}>
              {busy ? 'Uploading…' : 'Create job'}
            </button>
          </form>
          {error && <p className="error">{error}</p>}
        </section>

        {warnNoSurvey && (
          <section className="panel stop">
            <h3>No printer has been surveyed</h3>
            <p>
              Nothing can be printed on a machine that has not had its
              calibration print run — there is no way to know where the canvas
              sits on the page. <Link to="/printers">Set one up</Link>.
            </p>
          </section>
        )}
      </div>

      <section className="panel main">
        <div className="row-between">
          <h2>Jobs</h2>
          {archived.length > 0 && (
            <button className="link" aria-expanded={showArchived}
                    onClick={() => setShowArchived(!showArchived)}>
              {showArchived
                ? `Hide archived (${archived.length})`
                : `Show archived (${archived.length})`}
            </button>
          )}
        </div>
        {jobs === null && <p className="muted">Loading…</p>}
        {jobs !== null && emptyMessage && (
          <p className="muted">{emptyMessage}</p>
        )}
        {active.length > 0 && (
          <div className="bulk-bar">
            <label className="check">
              <input type="checkbox" ref={selectAllBox}
                     checked={selection.all} disabled={bulkBusy}
                     onChange={() => setSelected(
                       selection.all ? new Set() : new Set(activeIds))} />
              Select all
            </label>
            <button disabled={selection.count === 0 || bulkBusy}
                    onClick={() => void archiveSelected()}>
              {bulkBusy ? 'Archiving…' : `Archive selected (${selection.count})`}
            </button>
          </div>
        )}
        {bulkError && <p className="error">{bulkError}</p>}
        <ul className="rows">
          {active.map((job) => (
            <li key={job.jobId}>
              <input type="checkbox" className="row-check"
                     aria-label={`Select ${job.name}`}
                     checked={selected.has(job.jobId)} disabled={bulkBusy}
                     onChange={() => toggle(job.jobId)} />
              <Link className="job-thumb" to={`/jobs/${job.jobId}`}
                    aria-label={`Open ${job.name}`}>
                <Thumbnail job={job} size={100} />
              </Link>
              <div className="job-text">
                <Link className="job-title" to={`/jobs/${job.jobId}`}>
                  {job.name}
                </Link>
                <span className="muted">
                  {job.mesh}-mesh · {job.sheetCount} sheet(s) ·{' '}
                  {new Date(job.createdAt).toLocaleString()}
                </span>
              </div>
              <button className="link" disabled={archiving === job.jobId}
                      title="Hide this job from the list. Nothing is deleted."
                      onClick={() => void archive(job, true)}>
                {archiving === job.jobId ? 'Archiving…' : 'Archive'}
              </button>
            </li>
          ))}
        </ul>

        {showArchived && archived.length > 0 && (
          <>
            <h3 className="archive-head">Archived</h3>
            <p className="muted">
              Archiving only takes a job out of the list above. Its sheets,
              photographs, reports and print files are all still here, and
              the job page still opens.
            </p>
            <ul className="rows">
              {archived.map((job) => (
                <li key={job.jobId}>
                  <Link className="job-thumb" to={`/jobs/${job.jobId}`}
                        aria-label={`Open ${job.name}`}>
                    <Thumbnail job={job} size={100} />
                  </Link>
                  <div className="job-text">
                    <Link className="job-title" to={`/jobs/${job.jobId}`}>
                      {job.name}
                    </Link>
                    <span className="muted">
                      {job.mesh}-mesh · {job.sheetCount} sheet(s) · archived{' '}
                      {new Date(job.archivedAt!).toLocaleString()}
                    </span>
                  </div>
                  <button className="link" disabled={archiving === job.jobId}
                          onClick={() => void archive(job, false)}>
                    {archiving === job.jobId ? 'Restoring…' : 'Restore'}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
